import React from 'react';
import { Modal } from './Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { TableFilterState } from '../../hooks/useTableFilters';
import './AdvancedFilterModal.css';

export interface FilterOption {
    label: string;
    type: 'text' | 'select' | 'range' | 'checkbox' | 'multiselect';
    key: string;
    options?: { label: string; value: string | number }[];
    min?: number;
    max?: number;
    step?: number;
    placeholder?: string;
}

interface AdvancedFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    filters: TableFilterState;
    onFilterChange: (key: string, value: any) => void;
    onClearFilters: () => void;
    filterOptions: FilterOption[];
    title?: string;
}

export const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({
    isOpen,
    onClose,
    filters,
    onFilterChange,
    onClearFilters,
    filterOptions,
    title = 'Filter Options',
}) => {
    if (!isOpen) return null;

    const handleClearAndClose = () => {
        onClearFilters();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="filter-modal-wrapper">
            <div className="advanced-filter-modal">
                <div className="filter-modal-header">
                    <h2>{title}</h2>
                    <p className="filter-modal-subtitle">Refine your search with advanced filters</p>
                </div>

                <div className="filter-options">
                    {filterOptions.length === 0 ? (
                        <p style={{ gridColumn: '1 / -1', textAlign: 'center', opacity: 0.7 }}>No additional filters available</p>
                    ) : (
                        filterOptions.map(option => (
                            <div key={option.key} className="filter-group">
                                <label htmlFor={option.key} className="filter-label">
                                    {option.label}
                                </label>

                                {option.type === 'text' && (
                                    <input
                                        id={option.key}
                                        type="text"
                                        className="filter-input"
                                        placeholder={option.placeholder || `Filter by ${option.label.toLowerCase()}...`}
                                        value={filters[option.key] || ''}
                                        onChange={e => onFilterChange(option.key, e.target.value)}
                                    />
                                )}

                                {option.type === 'select' && (
                                    <select
                                        id={option.key}
                                        className="filter-select"
                                        value={filters[option.key] || ''}
                                        onChange={e => onFilterChange(option.key, e.target.value)}
                                    >
                                        <option value="">All</option>
                                        {option.options?.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {option.type === 'multiselect' && (
                                    <select
                                        id={option.key}
                                        className="filter-select"
                                        multiple
                                        value={Array.isArray(filters[option.key]) ? filters[option.key] : []}
                                        onChange={e => {
                                            const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                            onFilterChange(option.key, selected);
                                        }}
                                    >
                                        {option.options?.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {option.type === 'range' && (
                                    <div className="filter-range">
                                        <div className="range-info">
                                            <span className="range-label">{option.label}</span>
                                            <span className="range-value">
                                                {option.key.includes('Value') || option.key.includes('value')
                                                    ? `£${Math.round(filters[option.key] || option.min || 0).toLocaleString()}`
                                                    : Math.round(filters[option.key] || option.min || 0)}
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            className="filter-slider"
                                            min={
                                                option.key.includes('Value') || option.key.includes('value')
                                                    ? 0
                                                    : (option.min || 0)
                                            }
                                            max={
                                                option.key.includes('Value') || option.key.includes('value')
                                                    ? 50000000
                                                    : (option.max || 100)
                                            }
                                            step={
                                                option.key.includes('Value') || option.key.includes('value')
                                                    ? 100000
                                                    : (option.step || 1)
                                            }
                                            value={filters[option.key] || option.min || 0}
                                            onChange={e => {
                                                const sliderValue = parseFloat(e.target.value);
                                                onFilterChange(option.key, sliderValue);
                                            }}
                                        />
                                    </div>
                                )}

                                {option.type === 'checkbox' && (
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            className="filter-checkbox"
                                            checked={filters[option.key] || false}
                                            onChange={e => onFilterChange(option.key, e.target.checked)}
                                        />
                                        <span>{option.placeholder || 'Enable this filter'}</span>
                                    </label>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="filter-actions">
                    <button className="button danger small" onClick={handleClearAndClose}>
                        <FontAwesomeIcon icon={faXmark} style={{ marginRight: '6px' }} />
                        Clear Filters
                    </button>
                    <button className="button primary" onClick={onClose}>
                        Apply & Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};
